import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Search, X, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FAQ {
  id: string;
  category: string;
  category_icon: string;
  question: string;
  answer: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface GroupedFAQ {
  category: string;
  icon: React.ComponentType<any>;
  questions: FAQ[];
}

interface SearchableFAQProps {
  faqs: GroupedFAQ[];
  className?: string;
}

export const SearchableFAQ = ({ faqs, className = '' }: SearchableFAQProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  // Filtrage des FAQs basé sur la recherche et la catégorie
  const filteredFAQs = useMemo(() => {
    if (!searchTerm && !selectedCategory) return faqs;

    return faqs
      .map(category => ({
        ...category,
        questions: category.questions.filter(faq => {
          const matchesSearch = !searchTerm || 
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
          
          const matchesCategory = !selectedCategory || category.category === selectedCategory;
          
          return matchesSearch && matchesCategory;
        })
      }))
      .filter(category => category.questions.length > 0);
  }, [faqs, searchTerm, selectedCategory]);

  // Extraction de toutes les catégories pour les filtres
  const categories = useMemo(() => {
    return faqs.map(faq => faq.category);
  }, [faqs]);

  // Comptage des résultats
  const totalResults = filteredFAQs.reduce((acc, category) => acc + category.questions.length, 0);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
  };

  return (
    <div className={className}>
      {/* Barre de recherche et filtres */}
      <Card className="shadow-gentle border-0 mb-8">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Rechercher dans les questions fréquentes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filtres par catégorie */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory("")}
              >
                Toutes les catégories
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Informations sur les résultats */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {totalResults} résultat{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}
              </span>
              {(searchTerm || selectedCategory) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Effacer les filtres
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Résultats de recherche */}
      {filteredFAQs.length === 0 ? (
        <Card className="shadow-gentle border-0">
          <CardContent className="p-12 text-center">
            <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucun résultat trouvé</h3>
            <p className="text-muted-foreground mb-4">
              Essayez de modifier vos termes de recherche ou vos filtres.
            </p>
            <Button variant="outline" onClick={clearFilters}>
              Voir toutes les FAQ
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {filteredFAQs.map((category, categoryIndex) => (
            <Card key={categoryIndex} className="shadow-gentle border-0">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center text-white mr-4">
                    <category.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-display font-bold text-foreground">
                      {category.category}
                    </h2>
                    <Badge variant="secondary" className="mt-1">
                      {category.questions.length} question{category.questions.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                </div>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, index) => (
                    <AccordionItem 
                      key={faq.id} 
                      value={`${categoryIndex}-${index}`} 
                      className="border border-border rounded-lg px-6"
                    >
                      <AccordionTrigger className="text-left font-semibold hover:text-primary hover:no-underline">
                        {searchTerm ? (
                          <span dangerouslySetInnerHTML={{
                            __html: faq.question.replace(
                              new RegExp(`(${searchTerm})`, 'gi'),
                              '<mark class="bg-yellow-200 text-foreground rounded px-1">$1</mark>'
                            )
                          }} />
                        ) : (
                          faq.question
                        )}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed pt-4 pb-6">
                        {searchTerm ? (
                          <span dangerouslySetInnerHTML={{
                            __html: faq.answer.replace(
                              new RegExp(`(${searchTerm})`, 'gi'),
                              '<mark class="bg-yellow-200 text-foreground rounded px-1">$1</mark>'
                            )
                          }} />
                        ) : (
                          faq.answer
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};